/**
 * MisInformesTab — lists only the informes created by the current admin user,
 * with the same manage capabilities as the TechnicianPanel list:
 * Continuar (borrador), Ver detalle (non-borrador), Eliminar (borrador).
 *
 * Filters client-side by autorId === user.id since the API does not expose an
 * autorId query param. This is intentional: the full list is already small and
 * the filter is instant.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Search,
  FileText,
  Pencil,
  Trash2,
  Loader2,
  Plus,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/features/auth/index.js";
import {
  listInformes,
  deleteInforme,
  TIPO_LABEL,
  ESTADO_LABEL,
  type EstadoInforme,
  type Informe,
  type TipoInforme,
} from "@/features/reports/index.js";
import { StatusBadge } from "@/shared/ui/atoms/StatusBadge.js";
import { TipoBadge } from "@/shared/ui/atoms/TipoBadge.js";
import { Avatar } from "@/shared/ui/atoms/Avatar.js";
import { Input } from "@/shared/ui/input.js";
import { Button, buttonVariants } from "@/shared/ui/button.js";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/shared/ui/alert-dialog.js";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/ui/select.js";
import { friendlyMessage } from "@/shared/lib/errors.js";
import { cn } from "@/shared/lib/utils.js";

function formatFecha(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("es-PE");
}

const labelClass =
  "font-mono text-[0.625rem] font-medium uppercase tracking-[0.18em] text-muted-foreground";
const thClass =
  "px-4 py-3 font-mono text-[0.625rem] font-medium uppercase tracking-[0.12em]";
const accionClass =
  "h-8 gap-1 px-2.5 font-mono text-[0.625rem] uppercase tracking-[0.08em] whitespace-nowrap";

const TIPOS: TipoInforme[] = ["IE", "IS", "AC"];
const ESTADOS: EstadoInforme[] = ["borrador", "en_cola", "sincronizado", "enviado"];

interface MisInformesTabProps {
  /** Called after a mutation (delete) so the parent can refresh KPIs. */
  onChange?: () => void;
}

export function MisInformesTab({ onChange }: MisInformesTabProps) {
  const { user } = useAuth();
  const [todosInformes, setTodosInformes] = useState<Informe[]>([]);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busqueda, setBusqueda] = useState("");
  const [tipo, setTipo] = useState<TipoInforme | "">("");
  const [estado, setEstado] = useState<EstadoInforme | "">("");
  const [eliminandoId, setEliminandoId] = useState<string | null>(null);
  const [aEliminar, setAEliminar] = useState<Informe | null>(null);

  const cargar = useCallback(
    async (showSpinner = true) => {
      if (showSpinner) setCargando(true);
      setError(null);
      try {
        const data = await listInformes({
          ...(tipo ? { tipo } : {}),
          ...(estado ? { estado } : {}),
        });
        setTodosInformes(data);
      } catch (err) {
        setError(friendlyMessage(err));
      } finally {
        if (showSpinner) setCargando(false);
      }
    },
    [tipo, estado],
  );

  useEffect(() => {
    void cargar(true);
  }, [cargar]);

  // Filter to only the current admin's informes
  const misInformes = useMemo(
    () => todosInformes.filter((i) => i.autorId === user?.id),
    [todosInformes, user?.id],
  );

  const visibles = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    if (!q) return misInformes;
    return misInformes.filter(
      (i) => i.numero.toLowerCase().includes(q) || i.cliente.toLowerCase().includes(q),
    );
  }, [misInformes, busqueda]);

  async function confirmarEliminar() {
    const inf = aEliminar;
    if (!inf) return;
    setEliminandoId(inf.id);
    setError(null);
    try {
      await deleteInforme(inf.id);
      setTodosInformes((prev) => prev.filter((i) => i.id !== inf.id));
      toast.success("Informe eliminado");
      setAEliminar(null);
      onChange?.();
    } catch (err) {
      toast.error(friendlyMessage(err));
    } finally {
      setEliminandoId(null);
    }
  }

  return (
    <div className="space-y-6">
      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="rounded-3xl border border-border bg-card">
        {/* Toolbar: search + filters + new */}
        <div className="flex flex-col gap-3 p-4 sm:flex-row sm:flex-wrap sm:items-center md:p-6">
          <div className="relative w-full sm:max-w-sm">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="mis-inf-busqueda"
              value={busqueda}
              onChange={(e) => setBusqueda(e.target.value)}
              placeholder="Número o cliente…"
              className="rounded-full bg-secondary/40 pl-10"
            />
          </div>
          <div className="flex flex-col gap-2 sm:flex-1 sm:flex-row sm:flex-wrap sm:items-center">
            <Select value={tipo} onValueChange={(v) => setTipo(v as TipoInforme | "")}>
              <SelectTrigger className="w-full sm:w-40 rounded-full">
                <SelectValue placeholder="Tipo: todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Tipo: todos</SelectItem>
                {TIPOS.map((t) => (
                  <SelectItem key={t} value={t}>
                    {t} · {TIPO_LABEL[t]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={estado} onValueChange={(v) => setEstado(v as EstadoInforme | "")}>
              <SelectTrigger className="w-full sm:w-40 rounded-full">
                <SelectValue placeholder="Estado: todos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="">Estado: todos</SelectItem>
                {ESTADOS.map((s) => (
                  <SelectItem key={s} value={s}>
                    {ESTADO_LABEL[s]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <div className="ml-auto">
              <Link
                to="/informe/nuevo"
                className={cn(
                  buttonVariants({ size: "sm" }),
                  "gap-1.5 bg-brand-accent font-mono text-[0.625rem] uppercase tracking-[0.12em] text-brand-accent-foreground hover:bg-brand-accent/90",
                )}
              >
                <Plus className="size-3.5" aria-hidden />
                Nuevo
              </Link>
            </div>
          </div>
        </div>

        <div className="border-t border-border">
          {cargando ? (
            <p className="p-6 text-sm text-muted-foreground">Cargando informes…</p>
          ) : misInformes.length === 0 ? (
            <div className="p-8 text-center">
              <p className="text-sm text-muted-foreground">
                No creaste informes todavía.{" "}
                <Link
                  to="/informe/nuevo"
                  className="font-medium text-foreground underline-offset-2 hover:underline"
                >
                  Crear uno nuevo
                </Link>
              </p>
            </div>
          ) : visibles.length === 0 ? (
            <p className="p-6 text-sm text-muted-foreground">
              Sin resultados para «{busqueda}».
            </p>
          ) : (
            <>
              {/* Mobile: card list */}
              <ul className="divide-y divide-border md:hidden">
                {visibles.map((inf) => (
                  <li key={inf.id} className="flex items-start gap-3 p-4">
                    <TipoBadge tipo={inf.tipo} />
                    <div className="min-w-0 flex-1 space-y-1.5">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-foreground">{inf.numero}</span>
                        <StatusBadge estado={inf.estado} />
                      </div>
                      <div className="flex items-center gap-2">
                        <Avatar name={inf.cliente} />
                        <span className="truncate text-sm text-foreground">{inf.cliente}</span>
                      </div>
                      <p className="font-mono text-xs text-muted-foreground">
                        {formatFecha(inf.updatedAt)}
                      </p>
                      <div className="flex flex-wrap gap-1.5 pt-1">
                        {inf.estado === "borrador" && (
                          <Link
                            to={`/informe/${inf.id}`}
                            className={cn(
                              buttonVariants({ variant: "outline", size: "sm" }),
                              accionClass,
                            )}
                          >
                            <Pencil className="size-3" aria-hidden />
                            Continuar
                          </Link>
                        )}
                        {inf.estado !== "borrador" && (
                          <Link
                            to={`/informe/${inf.id}/detalle`}
                            className={cn(
                              buttonVariants({ variant: "outline", size: "sm" }),
                              accionClass,
                            )}
                          >
                            <FileText className="size-3" aria-hidden />
                            Ver detalle
                          </Link>
                        )}
                        {inf.estado === "borrador" && (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            disabled={eliminandoId === inf.id}
                            onClick={() => setAEliminar(inf)}
                            className={cn(
                              accionClass,
                              "border-destructive/40 text-destructive hover:bg-destructive/10",
                            )}
                          >
                            {eliminandoId === inf.id ? (
                              <Loader2 className="size-3 animate-spin" aria-hidden />
                            ) : (
                              <Trash2 className="size-3" aria-hidden />
                            )}
                            Eliminar
                          </Button>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>

              {/* Desktop: table */}
              <div className="hidden overflow-x-auto md:block">
                <table className="w-full min-w-[40rem] text-sm">
                  <thead className="border-b border-border text-left text-muted-foreground">
                    <tr>
                      <th className={thClass}>Número</th>
                      <th className={thClass}>Cliente</th>
                      <th className={thClass}>Estado</th>
                      <th className={thClass}>Actualizado</th>
                      <th className={cn(thClass, "text-right")}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {visibles.map((inf) => (
                      <tr
                        key={inf.id}
                        className="border-b border-border last:border-0 hover:bg-secondary/40"
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <TipoBadge tipo={inf.tipo} />
                            <span className="font-mono text-xs text-foreground">
                              {inf.numero}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2.5">
                            <Avatar name={inf.cliente} />
                            <span className="text-foreground">{inf.cliente}</span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge estado={inf.estado} />
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-muted-foreground">
                          {formatFecha(inf.updatedAt)}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex flex-wrap items-center justify-end gap-1.5">
                            {inf.estado === "borrador" && (
                              <Link
                                to={`/informe/${inf.id}`}
                                className={cn(
                                  buttonVariants({ variant: "outline", size: "sm" }),
                                  accionClass,
                                )}
                              >
                                <Pencil className="size-3" aria-hidden />
                                Continuar
                              </Link>
                            )}
                            {inf.estado !== "borrador" && (
                              <Link
                                to={`/informe/${inf.id}/detalle`}
                                className={cn(
                                  buttonVariants({ variant: "outline", size: "sm" }),
                                  accionClass,
                                )}
                              >
                                <FileText className="size-3" aria-hidden />
                                Ver detalle
                              </Link>
                            )}
                            {inf.estado === "borrador" && (
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                disabled={eliminandoId === inf.id}
                                onClick={() => setAEliminar(inf)}
                                className={cn(
                                  accionClass,
                                  "border-destructive/40 text-destructive hover:bg-destructive/10",
                                )}
                              >
                                {eliminandoId === inf.id ? (
                                  <Loader2 className="size-3 animate-spin" aria-hidden />
                                ) : (
                                  <Trash2 className="size-3" aria-hidden />
                                )}
                                Eliminar
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>

        {/* Footer: count */}
        <div className="flex items-center justify-between border-t border-border p-4 md:p-6">
          <span className={labelClass}>
            {cargando
              ? ""
              : `Mostrando ${visibles.length} informe${visibles.length === 1 ? "" : "s"}`}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled
              className="size-8 rounded-full"
            >
              <ChevronLeft className="size-4" aria-hidden />
            </Button>
            <Button
              type="button"
              variant="outline"
              size="icon"
              disabled
              className="size-8 rounded-full"
            >
              <ChevronRight className="size-4" aria-hidden />
            </Button>
          </div>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <AlertDialog
        open={aEliminar !== null}
        onOpenChange={(open) => {
          if (!open && eliminandoId === null) setAEliminar(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar borrador?</AlertDialogTitle>
            <AlertDialogDescription>
              Vas a eliminar {aEliminar?.numero} — {aEliminar?.cliente}. Esta acción no se puede
              deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel
              disabled={eliminandoId !== null}
              className="font-mono text-[0.625rem] uppercase tracking-[0.1em]"
            >
              Cancelar
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={eliminandoId !== null}
              onClick={(e) => {
                e.preventDefault();
                void confirmarEliminar();
              }}
              className="gap-1.5 font-mono text-[0.625rem] uppercase tracking-[0.1em]"
            >
              {eliminandoId !== null ? (
                <Loader2 className="size-3.5 animate-spin" aria-hidden />
              ) : (
                <Trash2 className="size-3.5" aria-hidden />
              )}
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
